// bluaIQ — curated ES/EN copy for contract-signing failures.
//
// THE DEFECT THIS FIXES. ContractSignPage rendered
//   {isEs ? 'Error al firmar' : 'Sign failed'}: {String(error?.message ?? '')}
// where `message` was the SERVER's English string. A Spanish-speaking
// contractor who opened an expired link, filled in their name, drew their
// signature and pressed Firmar was answered with:
//   «Error al firmar: Signing link expired»
// Half-translated, and system text besides.
//
// THE DOCTRINE (same one applied to the engine-jargon leak in the Excel export):
// the VIEW NEVER RENDERS SYSTEM TEXT. Server strings are wire protocol, not user
// copy. Every failure the signer can reach is mapped to a curated bilingual
// message that says what happened and what to do next; anything unrecognized
// falls back to a safe generic — it NEVER falls through to the raw string.
//
// CLASSIFICATION IS BY MACHINE CODE, NOT PROSE (Codex #479 P1). An earlier cut
// of this module split the two 401s by matching `/expired/i` against the
// server's English message. That reintroduced the very coupling this file
// exists to remove: a harmless reword on the server would have silently routed
// a contractor to the wrong screen. The server now emits a stable `code`
// (src/shared/contractSignErrorCodes.ts — the SAME module both sides import),
// and this module switches on it. No wire string is consulted anywhere.
//
// Unknown or missing codes — an older server during a rolling deploy, or a
// newer one this build has not learned — degrade to a curated message by
// status, never to raw text.

import {
  SIGN_ERROR_CODES,
  isSignErrorCode,
  type SignErrorCode,
} from './contractSignErrorCodes'

/** The failure states a signer can actually reach. */
export type SignErrorKey = 'invalid_or_used' | 'expired' | 'already_signed' | 'unknown'

/**
 * The prose the server also puts on the wire. Retained ONLY so a test can assert
 * that none of it ever reaches user-facing copy. It is never read to classify.
 * Source: src/server/contractSignTokenGuard.ts.
 */
export const SIGN_ERROR_WIRE_STRINGS = [
  'Invalid signing token',
  'Signing link expired',
  'Contract already signed',
] as const

/** code → curated copy key. Exhaustive over SIGN_ERROR_CODES. */
const KEY_BY_CODE: Record<SignErrorCode, SignErrorKey> = {
  [SIGN_ERROR_CODES.INVALID]: 'invalid_or_used',
  [SIGN_ERROR_CODES.EXPIRED]: 'expired',
  [SIGN_ERROR_CODES.ALREADY_SIGNED]: 'already_signed',
}

/**
 * Map an API failure to a copy key. Pure — no I/O, no React, NO prose.
 *
 * @param status HTTP status.
 * @param code   the server's machine code (`code` field). Anything unrecognized
 *               is ignored rather than trusted.
 */
export function classifySignError(status: number, code?: unknown): SignErrorKey {
  // The code decides whenever this build recognizes it.
  if (isSignErrorCode(code)) return KEY_BY_CODE[code]

  // No/unknown code — an older or newer server. Fall back on STATUS alone, which
  // is still structural. A 401 without a code cannot be split into
  // invalid-vs-expired, so use the conservative message that is true of both.
  if (status === 409) return 'already_signed'
  if (status === 401) return 'invalid_or_used'

  // 400 (malformed body), 5xx, network failure: say something honest.
  return 'unknown'
}

export type SignErrorCopy = { title: string; body: string }

/**
 * Curated bilingual copy. Every string is written for a contractor on a phone:
 * what happened, and the one action that resolves it. No status codes, no
 * server strings, no jargon.
 *
 * REGISTER (Codex #479 P1): Spanish addresses the signer as USTED — formal and
 * neutral ("Solicite", "su", "le", "Inténtelo", "comuníquese"). The signer is an
 * outside contractor receiving a legal document from a company they may never
 * have met; tuteo is too familiar for that relationship. The guard
 * contractSignErrorCopy.smoke.ts fails on informal forms and on the anglicism
 * "builder" in Spanish copy (the ES word is "constructor").
 */
export function signErrorCopy(key: SignErrorKey, isEs: boolean): SignErrorCopy {
  switch (key) {
    case 'invalid_or_used':
      return isEs
        ? {
            title: 'Este enlace ya no es válido',
            body: 'El enlace de firma no es válido o ya se usó. Solicite a su constructor que le envíe uno nuevo.',
          }
        : {
            title: 'This link no longer works',
            body: 'The signing link is invalid or has already been used. Ask your builder to send you a new one.',
          }
    case 'expired':
      return isEs
        ? {
            title: 'El enlace de firma venció',
            body: 'Por seguridad, los enlaces de firma vencen. Solicite a su constructor que le envíe uno nuevo.',
          }
        : {
            title: 'The signing link expired',
            body: 'Signing links expire for security. Ask your builder to send you a new one.',
          }
    case 'already_signed':
      return isEs
        ? {
            title: 'Este contrato ya está firmado',
            body: 'No hace falta firmarlo otra vez. Su constructor lo revisará y le avisará si necesita algo más.',
          }
        : {
            title: 'This contract is already signed',
            body: "You don't need to sign it again. Your builder will review it and let you know if anything else is needed.",
          }
    case 'unknown':
    default:
      return isEs
        ? {
            title: 'No pudimos completar la firma',
            body: 'Inténtelo de nuevo en unos minutos. Si el problema continúa, comuníquese con su constructor.',
          }
        : {
            title: "We couldn't complete the signature",
            body: 'Please try again in a few minutes. If it keeps happening, let your builder know.',
          }
  }
}
